<template>
  <main class="mx-auto w-full max-w-[1500px] px-5 py-8 sm:px-8 lg:px-10">
    <CreatorPlanLimitPrompt :open="showLimitPrompt" :resource="limitResource" @close="showLimitPrompt = false" />
    <BroadcastEditorModal
      :broadcast="editingBroadcast"
      :members="audienceOptions.members"
      :tiers="audienceOptions.tiers"
      :feature-instances="featureInstances"
      :sms-providers="smsProviders"
      :open="editorOpen"
      :saving="saving"
      :save-error="editorError"
      :save-success="editorSaveNotice"
      @close="closeEditor"
      @save="saveBroadcast"
      @autosave="autosaveBroadcast"
      @search-members="searchAudienceMembers"
    />

    <SmsChargeConfirmationModal
      :open="smsChargeModalOpen"
      :quote="pendingSmsQuote"
      :action="pendingSmsPayload?.deliveryAction || 'send'"
      :busy="smsChargeBusy"
      :error="smsChargeError"
      @close="closeSmsChargeModal"
      @confirm="confirmSmsCharge"
    />

    <Teleport to="body">
      <div v-if="deleteTarget" class="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-950/60 p-4" role="dialog" aria-modal="true" aria-label="Delete broadcast">
        <div class="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
          <div class="border-b border-slate-200 px-6 py-5">
            <div class="flex items-start justify-between gap-4">
              <div>
                <p class="text-xs font-bold uppercase tracking-[0.14em] text-red-500">Broadcast controls</p>
                <h2 class="mt-1 text-xl font-bold text-slate-900">{{ deleteTarget.name }}</h2>
              </div>
              <button type="button" class="broadcast-icon-button" aria-label="Close" @click="closeDeleteModal"><XMarkIcon class="h-5 w-5" /></button>
            </div>
          </div>

          <div class="space-y-5 p-6">
            <div v-if="deleteTarget.deleteScheduledAt" class="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p class="text-sm font-bold text-amber-900">Notification removal is scheduled</p>
              <p class="mt-1 text-sm text-amber-800">Members will no longer see this notification after {{ formatDateTime(deleteTarget.deleteScheduledAt) }}. The Sent record remains available here.</p>
              <button type="button" class="mt-3 text-sm font-bold text-blue-600 hover:text-blue-700" :disabled="deleting" @click="cancelScheduledDeletion">Cancel scheduled removal</button>
            </div>

            <div class="grid rounded-xl bg-slate-100 p-1" :class="canScheduleDeletion ? 'grid-cols-2' : 'grid-cols-1'">
              <button v-if="canScheduleDeletion" type="button" class="rounded-lg px-3 py-2.5 text-sm font-bold" :class="deleteMode === 'schedule' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'" @click="deleteMode = 'schedule'">Schedule notification removal</button>
              <button type="button" class="rounded-lg px-3 py-2.5 text-sm font-bold" :class="deleteMode === 'now' ? 'bg-white text-red-600 shadow-sm' : 'text-slate-500'" @click="deleteMode = 'now'">Delete now</button>
            </div>

            <div v-if="deleteMode === 'schedule'" class="rounded-xl border border-blue-100 bg-blue-50/60 p-4">
              <WebinarEventScheduler v-model:date="deleteForm.date" v-model:time="deleteForm.time" :timezone="deleteForm.timezone" />
              <label class="mt-4 block">
                <span class="mb-2 block text-xs font-bold text-slate-700">Time zone</span>
                <PlatformaSelect v-model="deleteForm.timezone" :options="timezoneOptions" />
              </label>
            </div>
            <p v-else class="rounded-xl border border-red-100 bg-red-50 p-4 text-sm leading-6 text-red-700">This removes the broadcast from your history. If it was already sent, the related member notifications are also removed.</p>

            <p v-if="deleteError" class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{{ deleteError }}</p>
          </div>

          <div class="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
            <button type="button" class="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-600" :disabled="deleting" @click="closeDeleteModal">Cancel</button>
            <button v-if="deleteMode === 'schedule'" type="button" class="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50" :disabled="deleting || !deleteForm.date || !deleteForm.time" @click="scheduleDeletion">{{ deleting ? 'Scheduling…' : 'Schedule notification removal' }}</button>
            <button v-else type="button" class="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50" :disabled="deleting" @click="deleteNow">{{ deleting ? 'Deleting…' : 'Delete broadcast' }}</button>
          </div>
        </div>
      </div>
    </Teleport>

    <header class="flex flex-col gap-5 border-b border-slate-200 pb-7 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <div class="mb-2 flex flex-wrap items-center gap-2">
          <span class="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-blue-600">Audience engagement</span>
          <span class="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">In-app delivery connected</span>
        </div>
        <h1 class="text-3xl font-bold tracking-tight text-slate-900">Broadcast</h1>
        <p class="mt-2 max-w-2xl text-sm leading-6 text-slate-500">Create rich in-app announcements for every member, a plan tier, or selected members, and schedule delivery when it matters.</p>
      </div>
      <button type="button" class="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-[#2D8CFF] px-5 py-3 text-sm font-bold text-white shadow-lg shadow-blue-500/20 transition hover:bg-[#247de7]" @click="createBroadcast">
        <PlusIcon class="h-5 w-5" aria-hidden="true" /> Create Broadcast
      </button>
    </header>

    <p v-if="pageNotice" class="mt-5 rounded-xl border px-4 py-3 text-sm" :class="pageNotice.type === 'error' ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'">{{ pageNotice.message }}</p>

    <section class="mt-7 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div class="flex flex-col gap-4 border-b border-slate-200 px-5 pt-5 sm:px-7">
        <div class="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div><h2 class="text-lg font-bold text-slate-900">Your broadcasts</h2><p class="mt-1 text-sm text-slate-500">Move between work in progress, upcoming sends, and delivery history.</p></div>
          <button type="button" class="text-xs font-bold text-blue-600 disabled:text-slate-400" :disabled="loading" @click="loadBroadcasts">{{ loading ? 'Refreshing…' : 'Refresh' }}</button>
        </div>

        <nav class="flex gap-1 overflow-x-auto" aria-label="Broadcast status">
          <button v-for="status in statuses" :key="status.id" type="button" class="relative flex shrink-0 items-center gap-2 px-4 pb-4 pt-2 text-sm font-semibold transition-colors" :class="activeStatus === status.id ? 'text-blue-600' : 'text-slate-500 hover:text-slate-900'" @click="activeStatus = status.id">
            {{ status.label }}
            <span class="rounded-full px-2 py-0.5 text-[11px]" :class="activeStatus === status.id ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-500'">{{ countForStatus(status.id) }}</span>
            <span class="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-blue-500 transition-opacity" :class="activeStatus === status.id ? 'opacity-100' : 'opacity-0'" />
          </button>
        </nav>
      </div>

      <div v-if="loading && !broadcasts.length" class="divide-y divide-slate-100" aria-busy="true" aria-label="Loading broadcasts">
        <article v-for="row in 3" :key="`broadcast-skeleton-${row}`" class="grid animate-pulse gap-4 px-5 py-5 sm:px-7 lg:grid-cols-[minmax(0,1fr)_180px_220px_auto] lg:items-center">
          <div class="min-w-0">
            <div class="h-4 w-48 max-w-[70%] rounded-full bg-slate-200" />
            <div class="mt-3 h-3 w-72 max-w-[88%] rounded-full bg-slate-100" />
          </div>
          <div class="flex items-center gap-2">
            <div class="h-5 w-5 rounded-full bg-slate-100" />
            <div class="h-3 w-28 rounded-full bg-slate-200" />
          </div>
          <div>
            <div class="h-3 w-36 rounded-full bg-slate-200" />
            <div class="mt-3 h-3 w-24 rounded-full bg-slate-100" />
          </div>
          <div class="flex items-center justify-end gap-2">
            <div class="h-9 w-9 rounded-xl bg-slate-100" />
            <div class="h-9 w-9 rounded-xl bg-slate-100" />
            <div class="h-9 w-9 rounded-xl bg-slate-100" />
          </div>
        </article>
      </div>
      <div v-else-if="visibleBroadcasts.length" class="divide-y divide-slate-100">
        <article v-for="broadcast in visibleBroadcasts" :key="broadcast.slug" class="group grid gap-4 px-5 py-5 transition hover:bg-slate-50/70 sm:px-7 lg:grid-cols-[minmax(0,1fr)_180px_220px_auto] lg:items-center">
          <button type="button" class="min-w-0 text-left" :disabled="broadcast.status === 'sent'" @click="editBroadcast(broadcast)">
            <div class="flex flex-wrap items-center gap-2">
              <h3 class="truncate text-sm font-bold text-slate-900 group-hover:text-blue-600">{{ broadcast.name }}</h3>
              <span v-if="broadcast.status === 'failed'" class="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-600">Failed</span>
              <span v-if="broadcast.deleteScheduledAt" class="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">Notification removal scheduled</span>
            </div>
            <p class="mt-1 line-clamp-1 text-sm text-slate-500">{{ broadcast.body || broadcast.title || 'Rich member broadcast' }}</p>
            <p v-if="broadcast.lastError" class="mt-1 line-clamp-1 text-xs text-red-500">{{ broadcast.lastError }}</p>
          </button>

          <div class="flex items-center gap-2 text-sm text-slate-600"><component :is="broadcast.channel === 'sms' ? ChatBubbleLeftRightIcon : BellAlertIcon" class="h-4 w-4 text-slate-400" />{{ broadcast.channel === 'sms' ? 'SMS Text Message' : 'In-App Notification' }}</div>
          <div class="text-sm"><p class="font-semibold text-slate-700">{{ timelineFor(broadcast) }}</p><p class="mt-0.5 text-xs text-slate-400">{{ audienceLabel(broadcast) }}</p></div>
          <div class="flex items-center justify-end gap-1">
            <button v-if="broadcast.status !== 'sent'" type="button" class="broadcast-icon-button broadcast-tooltip-group" aria-label="Edit broadcast" @click="editBroadcast(broadcast)"><PencilSquareIcon class="h-5 w-5" /><span class="broadcast-tooltip">Edit</span></button>
            <button type="button" class="broadcast-icon-button broadcast-tooltip-group" aria-label="Clone broadcast" @click="cloneBroadcast(broadcast)"><Square2StackIcon class="h-5 w-5" /><span class="broadcast-tooltip">Clone</span></button>
            <button type="button" class="broadcast-icon-button broadcast-icon-button--danger broadcast-tooltip-group" aria-label="Delete broadcast" @click="openDeleteModal(broadcast)"><TrashIcon class="h-5 w-5" /><span class="broadcast-tooltip broadcast-tooltip--right">Delete or schedule</span></button>
          </div>
        </article>
      </div>

      <div v-else class="flex min-h-[270px] flex-col items-center justify-center px-6 py-10 text-center">
        <div class="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400"><component :is="emptyStateIcon" class="h-7 w-7" /></div>
        <p class="mt-4 font-semibold text-slate-800">{{ emptyStateTitle }}</p>
        <p class="mt-1 max-w-md text-sm leading-6 text-slate-500">{{ emptyStateCopy }}</p>
        <button v-if="activeStatus === 'Drafts'" type="button" class="mt-5 text-sm font-bold text-blue-600 hover:text-blue-700" @click="createBroadcast">Create your first broadcast</button>
      </div>
    </section>

    <section class="mt-6 grid gap-4 md:grid-cols-3">
      <div v-for="item in workflowNotes" :key="item.title" class="rounded-2xl border border-slate-200 bg-white p-5"><component :is="item.icon" class="h-5 w-5 text-blue-500" /><h3 class="mt-3 text-sm font-bold text-slate-900">{{ item.title }}</h3><p class="mt-1 text-xs leading-5 text-slate-500">{{ item.copy }}</p></div>
    </section>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { BellAlertIcon, CalendarDaysIcon, ChatBubbleLeftRightIcon, ClockIcon, DocumentTextIcon, PaperAirplaneIcon, PencilSquareIcon, PlusIcon, Square2StackIcon, TrashIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import BroadcastEditorModal from '@/Components/BroadcastEditorModal.vue'
import SmsChargeConfirmationModal from '@/Components/SmsChargeConfirmationModal.vue'
import PlatformaSelect from '@/Components/PlatformaSelect.vue'
import WebinarEventScheduler from '@/Components/WebinarEventScheduler.vue'
import CreatorPlanLimitPrompt from '@/Components/CreatorPlanLimitPrompt.vue'
import { useWebsocketStore } from '@/stores/websocketStore'

const apiProxyUrl = String(process.env.VUE_APP_API_PROXY_URL || '').replace(/\/+$/, '')
const apiBase = `${apiProxyUrl}/api/creator-broadcasts`
const statuses = [{ id: 'Drafts', label: 'Drafts' }, { id: 'Scheduled', label: 'Scheduled' }, { id: 'Sent', label: 'Sent' }]
const timezoneOptions = [
  { value: 'America/New_York', label: 'Eastern Time' },
  { value: 'America/Chicago', label: 'Central Time' },
  { value: 'America/Denver', label: 'Mountain Time' },
  { value: 'America/Los_Angeles', label: 'Pacific Time' },
  { value: 'UTC', label: 'UTC' },
]
const workflowNotes = [
  { title: 'Build rich messages', copy: 'Combine text, imagery, video, and calls to action in the visual editor.', icon: DocumentTextIcon },
  { title: 'Schedule with confidence', copy: 'Target all members, a plan tier, or selected member accounts.', icon: CalendarDaysIcon },
  { title: 'Review delivery history', copy: 'Sent broadcasts remain available with their delivered member count.', icon: PaperAirplaneIcon },
]

const activeStatus = ref('Drafts')
const editorOpen = ref(false)
const editingBroadcast = ref(null)
const broadcasts = ref([])
const audienceOptions = reactive({ members: [], tiers: [] })
const featureInstances = ref([])
const smsProviders = ref([])
const loading = ref(false)
const saving = ref(false)
const editorError = ref('')
const showLimitPrompt = ref(false)
const limitResource = ref({})
const pageNotice = ref(null)
const editorSaveNotice = ref('')
const deleteTarget = ref(null)
const deleteMode = ref('schedule')
const deleting = ref(false)
const deleteError = ref('')
const smsChargeModalOpen = ref(false)
const pendingSmsQuote = ref(null)
const pendingSmsPayload = ref(null)
const smsChargeBusy = ref(false)
const smsChargeError = ref('')
const deleteForm = reactive({ date: '', time: '', timezone: 'America/New_York' })
const websocketStore = useWebsocketStore()
let noticeTimer = null
let editorSaveNoticeTimer = null
let refreshTimer = null
let unsubscribeBroadcastChanges = null
let autosaveInFlight = false
let queuedAutosavePayload = null

const statusGroup = (status) => ({ sent: 'Sent', scheduled: 'Scheduled', processing: 'Scheduled', draft: 'Drafts', failed: 'Drafts' }[status] || 'Drafts')
const visibleBroadcasts = computed(() => broadcasts.value.filter((item) => statusGroup(item.status) === activeStatus.value))
const countForStatus = (status) => broadcasts.value.filter((item) => statusGroup(item.status) === status).length
const emptyStateIcon = computed(() => activeStatus.value === 'Scheduled' ? ClockIcon : activeStatus.value === 'Sent' ? PaperAirplaneIcon : DocumentTextIcon)
const emptyStateTitle = computed(() => ({ Drafts: 'No draft broadcasts yet', Scheduled: 'Your broadcast schedule is clear', Sent: 'No broadcasts have been sent' }[activeStatus.value]))
const emptyStateCopy = computed(() => ({ Drafts: 'Start with the default template, then add the content blocks that fit your announcement.', Scheduled: 'Broadcasts prepared for future delivery will be organized here.', Sent: 'Delivery history appears here after an in-app broadcast is sent.' }[activeStatus.value]))
const canScheduleDeletion = computed(() => ['scheduled', 'processing', 'sent'].includes(deleteTarget.value?.status))

async function api(path = '', options = {}) {
  const response = await fetch(`${apiBase}${path}`, { credentials: 'include', cache: 'no-store', ...options, headers: { 'Content-Type': 'application/json', ...(options.headers || {}) } })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok || payload.success === false) {
    const error = new Error(payload.message || 'Broadcast request failed')
    error.payload = payload
    error.status = response.status
    throw error
  }
  return payload
}

async function loadSmsProviders() {
  try {
    const payload = await api('/sms/providers')
    smsProviders.value = Array.isArray(payload.providers) ? payload.providers : []
  } catch (error) {
    smsProviders.value = []
    editorError.value = error.message || 'Failed to load SMS providers.'
  }
}

async function loadBroadcasts() {
  loading.value = true
  try { const payload = await api('?limit=200'); broadcasts.value = Array.isArray(payload.broadcasts) ? payload.broadcasts : [] }
  catch (error) { setNotice('error', error.message || 'Failed to load broadcasts.') }
  finally { loading.value = false }
}

function setNotice(type, message) {
  if (noticeTimer) clearTimeout(noticeTimer)
  pageNotice.value = { type, message }
  noticeTimer = setTimeout(() => { pageNotice.value = null }, 30000)
}

function scheduleLiveRefresh(payload = {}) {
  if (refreshTimer) clearTimeout(refreshTimer)
  refreshTimer = setTimeout(async () => {
    const previousStatus = activeStatus.value
    await loadBroadcasts()
    if (payload.event === 'sent' && previousStatus === 'Scheduled') activeStatus.value = 'Sent'
  }, 120)
}

async function loadAudienceOptions() {
  try { const payload = await api('/audience-options'); audienceOptions.members = Array.isArray(payload.members) ? payload.members : []; audienceOptions.tiers = Array.isArray(payload.tiers) ? payload.tiers : [] }
  catch (error) { editorError.value = error.message || 'Failed to load member audience options.' }
}

async function loadFeatureInstances() {
  try {
    const response = await fetch(`${apiProxyUrl}/api/feature-instances`, { credentials: 'include', cache: 'no-store' })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(payload.message || 'Failed to load membership features.')
    const rows = payload.instances || payload.featureInstances || payload.items || (Array.isArray(payload) ? payload : [])
    featureInstances.value = (Array.isArray(rows) ? rows : []).map((feature) => ({
      id: Number(feature.instance_id ?? feature.feature_instance_id ?? feature.id ?? 0) || null,
      slug: String(feature.slug || feature.feature_slug || '').trim(),
      name: String(feature.custom_name || feature.customName || feature.feature_name || feature.featureName || feature.name || feature.slug || 'Membership feature').trim(),
    })).filter((feature) => feature.slug)
  } catch (error) {
    featureInstances.value = []
    editorError.value = error.message || 'Failed to load membership features.'
  }
}

async function searchAudienceMembers(query) {
  try {
    const suffix = query ? `?q=${encodeURIComponent(query)}` : ''
    const payload = await api(`/audience-options${suffix}`)
    audienceOptions.members = Array.isArray(payload.members) ? payload.members : []
    if (Array.isArray(payload.tiers)) audienceOptions.tiers = payload.tiers
  } catch (error) {
    editorError.value = error.message || 'Failed to search active members.'
  }
}

function clearEditorSaveNotice() {
  if (editorSaveNoticeTimer) clearTimeout(editorSaveNoticeTimer)
  editorSaveNoticeTimer = null
  editorSaveNotice.value = ''
}
function showEditorSaveNotice(message = 'Draft saved') {
  clearEditorSaveNotice()
  editorSaveNotice.value = message
  editorSaveNoticeTimer = setTimeout(clearEditorSaveNotice, 4500)
}
function createBroadcast() { editingBroadcast.value = null; editorError.value = ''; clearEditorSaveNotice(); editorOpen.value = true }
function editBroadcast(broadcast) { if (broadcast.status === 'sent') return; editingBroadcast.value = { ...broadcast }; editorError.value = ''; clearEditorSaveNotice(); editorOpen.value = true }
function closeEditor() { if (saving.value) return; editorOpen.value = false; editingBroadcast.value = null; editorError.value = ''; clearEditorSaveNotice() }

function closeSmsChargeModal() {
  if (smsChargeBusy.value) return
  smsChargeModalOpen.value = false
  pendingSmsQuote.value = null
  pendingSmsPayload.value = null
  smsChargeError.value = ''
}

function handlePlanLimitError(error) {
  if (error?.payload?.code !== 'creator_plan_limit_reached') return false
  const details = error.payload.details || {}
  limitResource.value = { key: details.limitKey || 'broadcasts', label: details.label || 'Broadcasts', usage: details.usage, publicLimit: details.publicLimit ?? details.limit, unit: details.unit || 'count', state: details.state || 'blocked' }
  showLimitPrompt.value = true
  return true
}

async function confirmSmsCharge() {
  if (!pendingSmsQuote.value?.quoteId || !pendingSmsPayload.value || smsChargeBusy.value) return
  smsChargeBusy.value = true
  smsChargeError.value = ''
  try {
    const chargeResult = await api('/sms/confirm-charge', { method: 'POST', body: JSON.stringify({ quoteId: pendingSmsQuote.value.quoteId }) })
    const payload = {
      ...pendingSmsPayload.value,
      smsQuoteId: pendingSmsQuote.value.quoteId,
      smsPaymentIntentId: chargeResult.charge?.paymentIntentId || '',
    }
    smsChargeModalOpen.value = false
    pendingSmsQuote.value = null
    pendingSmsPayload.value = null
    await saveBroadcast(payload)
  } catch (error) {
    smsChargeError.value = error.message || 'SMS payment failed.'
  } finally {
    smsChargeBusy.value = false
  }
}

async function autosaveBroadcast(payload) {
  queuedAutosavePayload = { ...payload, deliveryAction: 'draft', autosave: true }
  if (autosaveInFlight) return
  autosaveInFlight = true
  try {
    while (queuedAutosavePayload) {
      const nextPayload = queuedAutosavePayload
      queuedAutosavePayload = null
      const slug = String(nextPayload.slug || editingBroadcast.value?.slug || '').trim()
      const result = await api(slug ? `/${encodeURIComponent(slug)}` : '', { method: slug ? 'PUT' : 'POST', body: JSON.stringify({ ...nextPayload, slug }) })
      if (result.broadcast) {
        editingBroadcast.value = { ...result.broadcast }
        const index = broadcasts.value.findIndex((item) => item.slug === result.broadcast.slug)
        if (index >= 0) broadcasts.value.splice(index, 1, result.broadcast)
        else broadcasts.value.unshift(result.broadcast)
      }
      showEditorSaveNotice('Draft autosaved')
    }
  } catch (error) {
    if (!handlePlanLimitError(error)) editorError.value = error.message || 'Automatic draft save failed.'
  } finally {
    autosaveInFlight = false
  }
}

async function saveBroadcast(payload) {
  saving.value = true; editorError.value = ''
  try {
    if (payload.channel === 'sms' && payload.deliveryAction !== 'draft' && !payload.smsQuoteId) {
      const quoteResult = await api('/sms/quote', { method: 'POST', body: JSON.stringify(payload) })
      const quote = quoteResult.quote || {}
      if (payload.smsProviderMode === 'platforma_twilio') {
        pendingSmsQuote.value = quote
        pendingSmsPayload.value = { ...payload }
        smsChargeError.value = ''
        smsChargeModalOpen.value = true
        return
      }
      payload.smsQuoteId = quote.quoteId
    }
    const slug = String(payload.slug || '').trim()
    const result = await api(slug ? `/${encodeURIComponent(slug)}` : '', { method: slug ? 'PUT' : 'POST', body: JSON.stringify(payload) })
    await loadBroadcasts()
    activeStatus.value = payload.deliveryAction === 'send' ? 'Sent' : payload.deliveryAction === 'schedule' ? 'Scheduled' : 'Drafts'
    setNotice('success', payload.deliveryAction === 'send' ? `Broadcast sent to ${Number(result.recipientCount || 0)} member(s).` : payload.deliveryAction === 'schedule' ? 'Broadcast scheduled.' : 'Broadcast draft saved.')
    if (payload.deliveryAction === 'draft') {
      editingBroadcast.value = result.broadcast ? { ...result.broadcast } : { ...payload }
      showEditorSaveNotice('Draft saved')
    } else {
      clearEditorSaveNotice()
      editorOpen.value = false
      editingBroadcast.value = null
    }
  } catch (error) { if (!handlePlanLimitError(error)) editorError.value = error.message || 'Failed to save broadcast.' }
  finally { saving.value = false }
}

async function cloneBroadcast(broadcast) {
  try { await api(`/${encodeURIComponent(broadcast.slug)}/clone`, { method: 'POST', body: '{}' }); await loadBroadcasts(); activeStatus.value = 'Drafts'; setNotice('success', 'Broadcast cloned as a draft.') }
  catch (error) { if (!handlePlanLimitError(error)) setNotice('error', error.message || 'Failed to clone broadcast.') }
}

function openDeleteModal(broadcast) {
  deleteTarget.value = broadcast; deleteMode.value = ['scheduled', 'processing', 'sent'].includes(broadcast.status) ? 'schedule' : 'now'; deleteError.value = ''
  const tomorrow = new Date(Date.now() + 86400000)
  deleteForm.date = tomorrow.toISOString().slice(0, 10); deleteForm.time = '09:00'; deleteForm.timezone = broadcast.timezone || 'America/New_York'
}
function closeDeleteModal() { if (deleting.value) return; deleteTarget.value = null; deleteError.value = '' }

function zonedLocalToIso(dateText, timeText, timeZone) {
  const [year, month, day] = String(dateText).split('-').map(Number)
  const [hour, minute] = String(timeText).split(':').map(Number)
  if (![year, month, day, hour, minute].every(Number.isFinite)) return ''
  const target = Date.UTC(year, month - 1, day, hour, minute, 0)
  let guess = target
  const formatter = new Intl.DateTimeFormat('en-US', { timeZone, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })
  for (let pass = 0; pass < 2; pass += 1) {
    const parts = Object.fromEntries(formatter.formatToParts(new Date(guess)).filter((part) => part.type !== 'literal').map((part) => [part.type, Number(part.value)]))
    const rendered = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour === 24 ? 0 : parts.hour, parts.minute, parts.second)
    guess += target - rendered
  }
  return new Date(guess).toISOString()
}

async function scheduleDeletion() {
  deleting.value = true; deleteError.value = ''
  try {
    const deleteAt = zonedLocalToIso(deleteForm.date, deleteForm.time, deleteForm.timezone)
    if (!deleteAt || new Date(deleteAt).getTime() <= Date.now()) throw new Error('Choose a deletion time in the future.')
    if (deleteTarget.value.scheduledAt && new Date(deleteAt).getTime() <= new Date(deleteTarget.value.scheduledAt).getTime()) throw new Error('Schedule notification removal after the delivery time.')
    await api(`/${encodeURIComponent(deleteTarget.value.slug)}/deletion-schedule`, { method: 'POST', body: JSON.stringify({ deleteAt }) })
    await loadBroadcasts(); setNotice('success', 'Member notification removal scheduled.'); deleteTarget.value = null
  } catch (error) { deleteError.value = error.message || 'Failed to schedule deletion.' }
  finally { deleting.value = false }
}

async function cancelScheduledDeletion() {
  deleting.value = true; deleteError.value = ''
  try { await api(`/${encodeURIComponent(deleteTarget.value.slug)}/deletion-schedule`, { method: 'DELETE' }); await loadBroadcasts(); setNotice('success', 'Scheduled notification removal canceled.'); deleteTarget.value = null }
  catch (error) { deleteError.value = error.message || 'Failed to cancel scheduled deletion.' }
  finally { deleting.value = false }
}

async function deleteNow() {
  deleting.value = true; deleteError.value = ''
  try { await api(`/${encodeURIComponent(deleteTarget.value.slug)}`, { method: 'DELETE' }); await loadBroadcasts(); setNotice('success', 'Broadcast deleted.'); deleteTarget.value = null }
  catch (error) { deleteError.value = error.message || 'Failed to delete broadcast.' }
  finally { deleting.value = false }
}

function audienceLabel(broadcast) {
  if (broadcast.audience === 'tier') return audienceOptions.tiers.find((tier) => Number(tier.id) === Number(broadcast.audiencePlanTierId))?.name || 'Plan tier members'
  if (broadcast.audience === 'selected') return `${broadcast.audienceMemberIds?.length || 0} selected member(s)`
  return 'All active members'
}
function formatDateTime(value) { const date = value ? new Date(value) : null; return date && !Number.isNaN(date.getTime()) ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(date) : 'at the selected time' }
function timelineFor(broadcast) {
  if (broadcast.status === 'sent') return `${formatDateTime(broadcast.sentAt)} · ${Number(broadcast.deliveredCount || 0)} delivered · ${Number(broadcast.openRate || 0)}% opened · ${Number(broadcast.clickRate || 0)}% clicked`
  if (['scheduled', 'processing'].includes(broadcast.status)) return `Sends ${formatDateTime(broadcast.scheduledAt)}`
  return `Updated ${formatDateTime(broadcast.updatedAt)}`
}

onMounted(() => {
  void Promise.all([loadBroadcasts(), loadAudienceOptions(), loadFeatureInstances(), loadSmsProviders()])
  void websocketStore.connect()
  unsubscribeBroadcastChanges = websocketStore.subscribe('creator-broadcast:changed', scheduleLiveRefresh)
})

onBeforeUnmount(() => {
  if (noticeTimer) clearTimeout(noticeTimer)
  if (editorSaveNoticeTimer) clearTimeout(editorSaveNoticeTimer)
  if (refreshTimer) clearTimeout(refreshTimer)
  if (unsubscribeBroadcastChanges) unsubscribeBroadcastChanges()
})
</script>

<style scoped>
.broadcast-icon-button { display: inline-flex; height: 2.5rem; width: 2.5rem; align-items: center; justify-content: center; border-radius: .75rem; color: rgb(100 116 139); transition: color 150ms ease, background-color 150ms ease; }
.broadcast-icon-button:hover { background: rgb(226 232 240); color: rgb(37 99 235); }
.broadcast-icon-button--danger:hover { background: rgb(254 226 226); color: rgb(220 38 38); }
.broadcast-tooltip-group { position: relative; }
.broadcast-tooltip { pointer-events: none; position: absolute; bottom: calc(100% + .55rem); left: 50%; z-index: 50; width: max-content; max-width: 11rem; transform: translateX(-50%) translateY(.2rem); border-radius: .5rem; background: rgb(15 23 42 / .96); padding: .4rem .6rem; color: white; font-size: .7rem; font-weight: 700; line-height: 1rem; opacity: 0; box-shadow: 0 8px 24px rgb(15 23 42 / .2); transition: opacity 140ms ease, transform 140ms ease; }
.broadcast-tooltip::after { content: ''; position: absolute; left: 50%; top: 100%; transform: translateX(-50%); border: .3rem solid transparent; border-top-color: rgb(15 23 42 / .96); }
.broadcast-tooltip-group:hover .broadcast-tooltip, .broadcast-tooltip-group:focus-visible .broadcast-tooltip { opacity: 1; transform: translateX(-50%) translateY(0); }
.broadcast-tooltip--right { left: auto; right: 0; transform: translateY(.2rem); }
.broadcast-tooltip--right::after { left: auto; right: .75rem; transform: none; }
.broadcast-tooltip-group:hover .broadcast-tooltip--right, .broadcast-tooltip-group:focus-visible .broadcast-tooltip--right { transform: translateY(0); }
.broadcast-control { display: block; width: 100%; min-height: 2.75rem; border: 1px solid rgb(203 213 225); border-radius: .75rem; background: white; padding: .68rem .8rem; color: rgb(15 23 42); font-size: .8125rem; outline: none; }
.broadcast-control:focus { border-color: rgb(59 130 246); box-shadow: 0 0 0 3px rgb(59 130 246 / .12); }
</style>
